import { createContext, useContext } from 'react';

/**
 * The seam between `Field` (which owns the label, the message slot and §5's validation TIMING) and
 * the controls inside it (which own the value).
 *
 * It exists because every part of FS-3 FR-C5 that can be forgotten is wiring: an `aria-describedby` that
 * points nowhere, a label whose `htmlFor` does not match the control, an `aria-invalid` that never
 * arrives. Passing those by hand at every call site means they are right on the day the screen is
 * written and wrong after the next edit. Reading them from context makes the wiring the default and
 * omission the thing that takes effort.
 *
 * A control used OUTSIDE a `Field` still works: `useFieldControl` returns the control's own props
 * untouched when there is no provider. That is what lets `neutralRender` (subtask 12) render every
 * barrel export standalone, and what keeps a bare search input from needing a wrapper it has no use
 * for.
 */
export interface FieldContextValue {
    /**
     * The id `Field`'s `<label for>` points at.
     *
     * **`undefined` in `group` mode, deliberately.** A group field names a SET, so there is no
     * single control to point a label at — and handing one id to every control in the set would put
     * duplicate ids in the document, which is invalid and breaks every `getElementById`-shaped
     * lookup downstream (including the browser's own label resolution). Controls in a group keep
     * their own id, or none.
     */
    readonly controlId: string | undefined;
    /**
     * The message slot's id — present ONLY while the slot has text. An `aria-describedby` pointing
     * at an empty element reads as handled and announces nothing, which is worse than no
     * description at all, so the reference disappears with the text.
     */
    readonly messageId: string | undefined;
    /** True while the field is showing an ERROR, not merely helper text. */
    readonly invalid: boolean;
    readonly required: boolean;
    /** The control reports its value on blur; §5's timing rules live in `Field`, not here. */
    readonly reportBlur: (value: string) => void;
    /** The control reports its value on change. `Field` decides whether that re-validates. */
    readonly reportChange: (value: string) => void;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * What a control may state for itself, and what a surrounding `Field` may supply instead.
 *
 * Every member is `?: X | undefined` rather than plain `?: X`, because `exactOptionalPropertyTypes`
 * is on: every control reaches this hook by destructuring its own props, so it is always passing a
 * possibly-undefined value rather than omitting the key. Declaring "optional, and may be handed
 * undefined" is what that call actually does.
 */
export interface FieldControlOwnProps {
    readonly id?: string | undefined;
    readonly 'aria-describedby'?: string | undefined;
    readonly invalid?: boolean | undefined;
    readonly required?: boolean | undefined;
}

export interface FieldControlWiring {
    readonly id: string | undefined;
    readonly 'aria-describedby': string | undefined;
    /** `true` or absent — never `false`. `aria-invalid="false"` is legal but noisier than omitting. */
    readonly 'aria-invalid': true | undefined;
    readonly required: boolean;
    readonly reportBlur: (value: string) => void;
    readonly reportChange: (value: string) => void;
}

const noop = () => {};

/**
 * Merge a control's own accessibility props with the surrounding field's.
 *
 * **The field's `controlId` wins over a control's own `id`, deliberately.** Inside a `Field` the
 * label association is the field's job, and a control that quietly kept its own id would leave the
 * `<label for>` pointing at nothing — a failure that looks like nothing on screen and costs a
 * screen-reader user the label entirely. Set the id on the `Field` instead; it accepts one.
 *
 * `aria-describedby` is the opposite case and is MERGED rather than replaced: a control may
 * legitimately be described by something else as well (a format hint, a character counter), and
 * dropping that to make room for the field's message would trade one description for another.
 */
export function useFieldControl(own: FieldControlOwnProps): FieldControlWiring {
    const field = useContext(FieldContext);

    const describedBy = [own['aria-describedby'], field?.messageId].filter((id) => id !== undefined);

    return {
        id: field?.controlId ?? own.id,
        'aria-describedby': describedBy.length === 0 ? undefined : describedBy.join(' '),
        'aria-invalid': own.invalid === true || field?.invalid === true ? true : undefined,
        required: own.required ?? field?.required ?? false,
        reportBlur: field?.reportBlur ?? noop,
        reportChange: field?.reportChange ?? noop,
    };
}
